/**
 * Tenant-switch invalidation logic for the offline IndexedDB.
 *
 * Per design §5.2.1 (and the corresponding plan issue 3.3): the offline
 * IndexedDB stores are NOT namespaced per tenant. On a shared tablet, when
 * a different user (on a different tenant) logs in, every store must be
 * wiped before the new tenant's data is loaded — otherwise stale layers,
 * sessions, or ETag-cached payloads from tenant A could leak to tenant B.
 *
 * BUT: a clear must NEVER silently destroy unsynced offline checks. If the
 * previous tenant left sessions in `collecting`, `ready_to_sync`, or
 * `syncing` status, the controller must be prompted before any data is
 * lost (epic §3 "Reliability" / "no silent data loss"). The prompt is a
 * non-dismissible modal — see `OfflineTenantInvalidator.tsx` for the UI.
 *
 * This module is pure logic with no React deps so it's straightforward to
 * test, and so the React component above can compose it without worrying
 * about hook ordering.
 */

import {
    clearOfflineDB,
    getOfflineDB,
    resetOfflineDBConnection,
    STORE_SESSIONS,
} from "./db";
import type { OfflineSessionStatus } from "./types";

const STORAGE_KEY = "avos-offline-tid";

/**
 * Statuses that count as "the user has work that hasn't reached the
 * server yet". Sessions in `synced` are safely on the server already;
 * `failed` sessions are terminal (the design preserves them for the
 * controller's reference but does not block sync). Wiping either is OK.
 */
const PENDING_STATUSES: ReadonlyArray<OfflineSessionStatus> = [
    "collecting",
    "ready_to_sync",
    "syncing",
];

/**
 * Returns true if `tenantId` differs from the value last stored in
 * localStorage. Absent storage (first run, or quota error on the previous
 * write) counts as "needs clearing" — better to clear than risk leakage.
 */
export function needsInvalidation(tenantId: string): boolean {
    return readStoredTenantId() !== tenantId;
}

/**
 * Count of offline-sessions rows currently in a pending status. Used by
 * the React component to decide between silent clear (0) and modal
 * prompt (>0). Cheap: the `by-status` index lets us count per status
 * without iterating.
 */
export async function getPendingSessionCount(): Promise<number> {
    const db = await getOfflineDB();
    const index = db.transaction(STORE_SESSIONS).store.index("by-status");
    const counts = await Promise.all(
        PENDING_STATUSES.map((status) => index.count(IDBKeyRange.only(status))),
    );
    return counts.reduce((acc, n) => acc + n, 0);
}

/**
 * Clear every store and record the new tenant id. The caller is
 * responsible for confirming this is safe to do (no pending sessions, OR
 * the controller has explicitly chosen "Discard and continue" via the
 * modal's second-step confirmation). This function does not check that
 * itself — by design, so unit tests can drive each path explicitly.
 *
 * The localStorage write happens last; if `clearOfflineDB()` throws we
 * leave the previous tid in place so the next render retries.
 */
export async function clearAndRecord(tenantId: string): Promise<void> {
    await clearOfflineDB();
    resetOfflineDBConnection();
    writeStoredTenantId(tenantId);
}

function readStoredTenantId(): string | null {
    try {
        return window.localStorage.getItem(STORAGE_KEY);
    } catch {
        // localStorage can throw in some edge cases (private browsing on iOS,
        // disabled storage). Treat as "no stored value".
        return null;
    }
}

function writeStoredTenantId(tenantId: string): void {
    try {
        window.localStorage.setItem(STORAGE_KEY, tenantId);
    } catch {
        // See readStoredTenantId.
    }
}

export const _internals_for_tests = {
    STORAGE_KEY,
    PENDING_STATUSES,
};
